import { motion } from 'motion/react';
import { Exchange, Segment } from '@/types/symbols/exchangeTypes';
import { formatAmount, formatToman, getAmount, getPrice } from '@/utils/symbols/exchangeUtils';
import { TableCell, TableRow } from '@/components/ui/table';
import Link from 'next/link';
import Image from 'next/image';
import { Star } from 'lucide-react';
import { toPersianDigits } from '@/utils/persianUtils';
import { sitePath } from '@/utils/links/safeHref';

// Create a motion-compatible component from TableRow for animations
const MotionTableRow = motion(TableRow);

interface ExchangeRowProps {
    exchange: Exchange;
    index: number;
    segment: Segment;
    isBestPrice: boolean;
    flash?: 'up' | 'down';
}

const ExchangeRow: React.FC<ExchangeRowProps> = ({
    exchange,
    index,
    segment,
    isBestPrice,
    flash,
}) => {
    const price = getPrice(exchange, segment);
    const amount = getAmount(exchange, segment);

    // Animation logic for background flash
    const bgAnim =
        flash === 'up'
            ? ['rgba(46, 204, 113, 0.25)', 'rgba(0, 0, 0, 0)'] // Green flash
            : flash === 'down'
              ? ['rgba(231, 76, 60, 0.25)', 'rgba(0, 0, 0, 0)'] // Red flash
              : 'rgba(0, 0, 0, 0)'; // Transparent

    return (
        <MotionTableRow
            layout
            className={'dark:border-white/10'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, backgroundColor: bgAnim as any }}
            exit={{ opacity: 0 }}
            transition={{
                backgroundColor: { duration: 1.2 },
                layout: { type: 'spring', stiffness: 400, damping: 30 },
            }}
        >
            {/* Column 1: Index */}
            <TableCell className='text-[11px] text-center opacity-70 w-[36px] min-w-[36px] whitespace-nowrap px-2'>
                {index + 1}
            </TableCell>

            {/* Column 2: Exchange Name, Logo & Rating */}
            <TableCell className='min-w-[140px] whitespace-nowrap py-2'>
                <div className='flex items-center gap-2 text-sm'>
                    {exchange.logo ? (
                        <Image
                            src={exchange.logo}
                            alt={exchange.name}
                            width={22}
                            height={22}
                            className='w-5.5 h-5.5 rounded-full object-cover shrink-0'
                        />
                    ) : (
                        <div className='w-5.5 h-5.5 rounded-full bg-gray-200 shrink-0' />
                    )}
                    <div className='flex flex-col items-start'>
                        <Link
                            // The `|| '#'` never applied: a missing slug built /exchange/undefined
                            href={sitePath('exchange', exchange?.slug) ?? '#'}
                            className='font-medium whitespace-nowrap text-xs sm:text-sm hover:text-blue-600 dark:hover:text-blue-400 transition-colors'
                        >
                            {exchange.name}
                        </Link>
                        {exchange.rating && exchange.rating > 0 ? (
                            <div
                                className='inline-flex items-center gap-1 mt-0.5'
                                title={`امتیاز: ${exchange.rating} از ۵`}
                            >
                                <div className='relative w-[14px] h-[14px] flex items-center justify-center shrink-0'>
                                    {/* Background Outline Star */}
                                    <Star
                                        size={14}
                                        className='stroke-yellow-500 fill-transparent shrink-0'
                                        strokeWidth={1.5}
                                    />
                                    {/* Foreground Golden Filled Star clipped by percentage */}
                                    <div
                                        className='absolute inset-0 overflow-hidden text-yellow-500'
                                        style={{ width: `${(exchange.rating / 5) * 100}%` }}
                                    >
                                        <Star
                                            size={14}
                                            className='fill-yellow-500 stroke-yellow-500 shrink-0'
                                            strokeWidth={1.5}
                                        />
                                    </div>
                                </div>
                                <span className='text-[10px] text-gray-500 dark:text-gray-400 font-medium'>
                                    {toPersianDigits(exchange.rating)}
                                </span>
                            </div>
                        ) : null}
                    </div>
                </div>
            </TableCell>

            {/* Column 3: Price */}
            <TableCell
                className={`text-right text-xs whitespace-nowrap ${isBestPrice ? 'font-bold text-emerald-600 dark:text-emerald-400' : ''}`}
                dir='ltr'
            >
                {formatToman(price)}
            </TableCell>

            {/* Column 4: Amount */}
            <TableCell className='text-right text-xs whitespace-nowrap' dir='ltr'>
                {amount === 0 ? 'نامحدود' : formatAmount(amount)}
            </TableCell>

            {/* Column 5: Action Button */}
            <TableCell className='text-center whitespace-nowrap w-[90px] min-w-[90px]'>
                <a
                    href={exchange.registerLink || '#'}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-block'
                >
                    <div
                        className={`inline-flex items-center justify-center px-3 py-1.5 text-xs font-semibold rounded-xl transition-all duration-200 hover:scale-105 shadow-2xs ${
                            segment === 'buy'
                                ? 'bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30'
                                : 'bg-rose-500/15 hover:bg-rose-500/25 text-rose-700 dark:text-rose-300 border border-rose-500/30'
                        }`}
                    >
                        {segment === 'buy' ? 'خرید' : 'فروش'}
                    </div>
                </a>
            </TableCell>
        </MotionTableRow>
    );
};

export default ExchangeRow;
